<template>
  <main>
    <b-container v-if="currentModel && project">
      <div class="page-breadcrumb">
        <router-link
          :to="{ name: 'ProjectView', params: { id: project._id } }"
          class="border-0"
        >
          <i class="fa fa-angle-left" /> Back to project:
          {{ project.name }}
        </router-link>
      </div>

      <div class="section-header">
        <h1 class="heading">{{ currentModel.name }}</h1>

        <div class="actions">
          <div class="action">
            <b-btn variant="outline-secondary" class="mr-1" @click="confirmDelete">
              <i class="fa fa-trash" />
              Delete model
            </b-btn>
          </div>
          <div class="action">
            <b-btn
              variant="primary"
              @click="
                $router.push({
                  name: 'ModelEdit',
                  params: {
                    modelId: currentModel._id,
                    projectId: project._id,
                  },
                })
              "
            >
              <i class="fa fa-pencil-alt" />
              Edit model
            </b-btn>
          </div>
        </div>
      </div>
      
      <div class="layoutcard">
        <table class="table table-hover vertical-align-middle">
          <tbody>
            <tr>
              <th>Description</th>
              <td>{{ currentModel.description }}</td>
            </tr>
            <tr>
              <th>Status</th>
              <td>{{ currentModel.status.label }}</td>
            </tr>
            <tr>
              <th>Created on</th>
              <td>
                {{ currentModel.creationDate | moment("DD-MM-YYYY HH:mm") }}
              </td>
            </tr>
            <tr>
              <th>Created by</th>
              <td>{{ getUserInfo(currentModel.created_by) }}</td>
            </tr>
            <tr>
              <th>Last modified on</th>
              <td>
                {{ currentModel.lastModified | moment("DD-MM-YYYY HH:mm") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section-header">
        <h1 class="heading">Settings</h1>
        <div class="actions"></div>
      </div>

      <div class="layoutcard">
        <b-form @submit.prevent="submitSettings">
          <b-form-row>
            <b-col cols="2">
              <b-form-group
                id="input-group-lower-limit"
                label="Lower limit"
                label-for="input-lower-limit"
                class="mb-4"
              >
                <b-form-input
                  id="input-lower-limit"
                  v-model="form.lowerLimit"
                  v-validate="{ numeric: true, min_value: 2 }"
                  placeholder="Default: 4"
                  data-vv-name="lower limit"
                  number
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="2">
              <b-form-group
                id="input-group-upper-limit"
                label="Upper limit"
                label-for="input-upper-limit"
                class="mb-4"
              >
                <b-form-input
                  id="input-upper-limit"
                  v-model="form.upperLimit"
                  v-validate="{ numeric: true }"
                  placeholder="Default: none"
                  data-vv-name="upper limit"
                  number
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="2">
              <b-form-group
                id="input-group-ratio"
                label="Ratio"
                label-for="input-ratio"
                class="mb-4"
              >
                <b-form-input
                  id="input-ratio"
                  v-model="form.ratio"
                  v-validate="{ decimal: 1, min_value: 1 }"
                  type="text"
                  placeholder="Default: 1.1"
                  data-vv-name="ratio"
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col cols="2">
              <b-form-group
                id="input-group-max-word-count"
                label="Max word count"
                label-for="input-max-word-count"
                class="mb-4"
              >
                <b-form-input
                  id="input-max-word-count"
                  v-model="form.maxWordCount"
                  v-validate="{ numeric: true, min_value: 500, max_value: 32000 }"
                  placeholder="Default: 32000"
                  data-vv-name="max word count"
                  number
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="2">
              <b-form-group
                id="input-group-number-of-words"
                label="Number of words"
                label-for="input-number-of-words"
                class="mb-4"
              >
                <b-form-input
                  id="input-number-of-words"
                  v-model="form.numberOfWords"
                  v-validate="{ numeric: true, min_value: 1 }"
                  placeholder="Default: 2"
                  data-vv-name="number of words"
                  number
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="2">
              <b-form-group
                id="input-group-number-of-fields"
                label="Number of fields"
                label-for="input-number-of-fields"
                class="mb-4"
              >
                <b-form-input
                  id="input-number-of-fields"
                  v-model="form.numberOfFields"
                  v-validate="{ numeric: true, min_value: 10, max_value: 250 }"
                  placeholder="Default: 150"
                  data-vv-name="number of fields"
                  number
                  :disabled="currentModel.status.code > 1"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col>
              <b-form-group
                id="input-group-batching"
                label="Batching"
                label-for="input-batching"
                class="mb-4"
              >
                <b-form-checkbox v-model="form.batching" size="sm" switch
                  >{{ form.batching ? 'Enabled' : 'Disabled' }}</b-form-checkbox
                >
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-btn variant="primary" type="submit"
            ><i class="fa fa-update" /> Save settings</b-btn
          >
        </b-form>
      </div>

      <div class="section-header">
        <h1 class="heading">Pools</h1>

        <div class="actions">
          <div class="action">
            <b-btn
              variant="primary"
              @click="addPool"
            >
              <i class="fa fa-plus" />
              Add pool
            </b-btn>
          </div>
        </div>
      </div>

      <div class="layoutcard">
        <table class="table table-hover vertical-align-middle">
          <tbody>
            <tr v-for="(pool, i) in currentModel.pools" :key="i">
              <td>
                {{ getPool(pool.pool_id).name | ucFirst }}
              </td>
              <td class="small">
                {{ getPool(pool.pool_id).dbtype }}
              </td>
              <td class="small text-muted">
                {{ getPool(pool.pool_id).status.label }}
              </td>
              <td class="text-muted text-right">
                {{ getPool(pool.pool_id).creationDate | moment("DD-MM-YYYY") }}
              </td>
              <td class="text-right">
                <b-btn-group>
                  <b-btn
                    variant="outline-secondary"
                    size="sm"
                    @click="
                      $router.push({
                        name: 'PoolView',
                        params: { id: pool.pool_id, ref: 'default' },
                      })
                    "
                  >
                    <i class="fa fa-eye" />
                    View Pool
                  </b-btn>
                  <b-dropdown
                    size="sm"
                    variant="outline-secondary"
                    dropup
                    toggle-class="text-decoration-none"
                    no-caret
                  >
                    <template slot="button-content"
                      ><i class="fa fa-ellipsis-h"
                    /></template>
                    <b-dropdown-item @click="confirmRemovePoolModel(pool._id)"
                      >Remove pool</b-dropdown-item
                    >
                  </b-dropdown>
                </b-btn-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!--
      <div class="section-header">
        <h1 class="heading">Users</h1>

        <div class="actions">
          <div class="action">
            <b-btn variant="primary" @click="addUser">
              <i class="fa fa-plus" />
              Add user
            </b-btn>
          </div>
        </div>
      </div>

      <table class="table table-hover vertical-align-middle mb-5">
        <tbody>
          <tr v-for="(user, i) in currentModel.userfilters" :key="i">
            <td v-if="user">
              <router-link :to="{ name: 'UserView', params: { id: user.user } }">
                {{ getUserInfo(user.user) }}
              </router-link>
            </td>
            <td class="text-right">
              <b-btn-group v-if="currentModel.userfilters.length > 1">
                <b-btn
                  variant="outline-secondary"
                  size="sm"
                  @click="confirmDeleteUser(user.user)"
                >
                  <i class="fa fa-ban" />
                </b-btn>
              </b-btn-group>
            </td>
          </tr>
        </tbody>
      </table>

      <UserSelectModal
        v-if="users.length > 0"
        :users="userOptions"
        :header="'Add user'"
        :size="'sm'"
        @submit="addUserSubmit"
      ></UserSelectModal>

      -->

      <PoolSelectModal
        :header="'Add pool'"
        :size="'sm'"
        @submit="addPoolSubmit"
      ></PoolSelectModal>
    </b-container>
  </main>
</template>

<script>
import _ from "lodash";
import { setTimeout } from "timers";
import { mapActions, mapGetters, mapState } from "vuex";
import InitAdmin from "../Shared/InitAdmin";
import PoolSelectModal from "../Shared/PoolSelectModal";
import UserSelectModal from "../Shared/UserSelectModal";

export default {
  name: "ModelView",
  components: {
    UserSelectModal,
    PoolSelectModal,
  },
  extends: InitAdmin,
  data() {
    return {
      interval: null,
      currentModel: null,
      project: null,
      form: {
        lowerLimit: null,
        upperLimit: null,
        ratio: null,
        maxWordCount: null,
        numberOfWords: null,
        numberOfFields: null,
        batching: true
      },
    };
  },
  computed: {
    ...mapGetters(["poolById"]),
    ...mapState({
      users: (state) => state.user.users,
    }),
    userOptions() {
      // Get full user info from project users, remove the ones that are already in the model
      const selection = this.users
        .filter((user) => this.project.users.some((u) => u.user === user._id))
        .filter(
          (user) =>
            !this.currentModel.userfilters.some((u) => u.user === user._id)
        );

      return _.sortBy(selection, "full_name");
    },
  },
  mounted() {
    //this.interval = setInterval(this.fetchData, 10000);
    this.fetchData();
  },
  //destroyed() {
  //  clearInterval(this.interval);
  //},
  methods: {
    ...mapActions([
      "removeModel",
      "addUserToModel",
      "removeUserFromModel",
      "addPoolToModel",
      "removePoolFromModel",
      "loadProjectModels",
      "loadProject",
      "loadModel",
      "updateModel",
    ]),
    fetchData() {
      this.loadProject({ id: this.$route.params.projectId }).then((res) => {
        this.project = res;
      });
      this.loadModel({ id: this.$route.params.modelId }).then((res) => {
        this.currentModel = res;
        this.form.lowerLimit = this.currentModel.config.ll;
        this.form.upperLimit = this.currentModel.config.ul;
        this.form.ratio = this.currentModel.config.ratio;
        this.form.maxWordCount = this.currentModel.config.maxcount;
        this.form.numberOfWords = this.currentModel.config.number_of_words;
        this.form.numberOfFields = this.currentModel.config.n_fields;
        this.form.batching = 'batching' in this.currentModel.config ? this.currentModel.config.batching : true;
      });
    },
    getPool(id) {
      return this.poolById({ id: id });
    },
    addPool() {
      this.$bvModal.show("modal-select-pool");
    },
    addPoolSubmit(pool) {
      this.addPoolToModel({
        projectId: this.project._id,
        modelId: this.currentModel._id,
        poolId: pool._id,
      }).then(() => {
        this.$toasted.show("Pool added to model");
        this.fetchData();
      });
    },
    confirmRemovePoolModel(poolId) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to remove this pool?",
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.removePoolFromModel({
              projectId: this.project._id,
              modelId: this.currentModel._id,
              poolId: poolId,
            }).then(() => {
              this.$toasted.show("Pool removed from model");
              this.fetchData();
            });
          }
        });
    },
    confirmDelete() {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to delete the model “${this.currentModel.name}”?`,
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.removeModel({ id: this.currentModel._id }).then(() => {
              this.$toasted.show("Model deleted");
              setTimeout(() => {
                this.$router.push({
                  name: "ProjectView",
                  params: { id: this.project._id },
                });
              }, 1000);
            });
          }
        });
    },
    confirmDeleteUser(userId) {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to remove "${this.getUserInfo(
            userId
          )}" from this model?`,
          this.$confirmOptions
        )
        .then((value) => {
          if (value) {
            this.removeUserFromModel({
              projectId: this.project._id,
              modelId: this.currentModel._id,
              userId: userId,
            }).then(() => {
              this.$toasted.show("User removed from model");
              this.fetchData();
            });
          }
        });
    },
    getUserInfo(userId) {
      const user = this.users.find((u) => u._id === userId);
      return user ? user.full_name : null;
    },
    addUser() {
      this.$bvModal.show("modal-select-user");
    },
    addUserSubmit(user) {
      this.addUserToModel({
        projectId: this.project._id,
        modelId: this.currentModel._id,
        userId: user._id,
      }).then(() => {
        this.$toasted.show("User added to model");
        this.fetchData();
      });
    },
    submitSettings() {
      this.$validator.validateAll().then((result) => {
        if (!result) {
          this.$toasted.show(this.$validator.errors.items[0].msg, {
            type: "error",
          });
          return;
        }
        if (
          this.form.lowerLimit &&
          this.form.upperLimit &&
          this.form.upperLimit <= this.form.lowerLimit
        ) {
          this.$toasted.show("Lower limit should be less than upper limit", {
            type: "error",
          });
          return;
        }
        if (
          this.form.lowerLimit &&
          this.form.upperLimit &&
          this.form.upperLimit - this.form.lowerLimit < 2
        ) {
          this.$toasted.show(
            "Difference between lower limit and upper limit should be at least 2",
            { type: "error" }
          );
          return;
        }
        this.updateModel({
          modelId: this.$route.params.modelId,
          model: {
            name: this.form.modelName,
            description:
              this.form.modelDescription === null
                ? "NA"
                : this.form.modelDescription,
            config: {
              ll: this.form.lowerLimit,
              ul: this.form.upperLimit,
              ratio: parseFloat(this.form.ratio),
              maxcount: this.form.maxWordCount,
              number_of_words: this.form.numberOfWords,
              n_fields: this.form.numberOfFields,
              batching: this.form.batching
            },
          },
        }).then(() => {
          this.$toasted.show("Model Updated");
          setTimeout(() => {
            this.$router.push({
              name: "ModelView",
              params: {
                modelId: this.currentModel._id,
                projectId: this.project._id,
              },
            });
          }, 1000);
        });
      });
    },
  },
};
</script>
